import { useCallback, useState } from 'react';
import { api } from '../api/client';
import { useToast } from '../components/Toast';
import { addPageAction, noticeError } from '../newrelic';

export default function Upload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [lastResponse, setLastResponse] = useState(null);
  const toast = useToast();

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setLastResponse(null);

    try {
      const res = await api.uploadFile(file);
      addPageAction('file_uploaded', { fileName: file.name, fileSize: file.size });
      setLastResponse(res);
      toast('File uploaded successfully', 'success');
      setFile(null);
    } catch (err) {
      noticeError(err, { page: 'upload', fileName: file.name });
      toast(err.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const onDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]);
  }, []);

  const dropZoneStyle = {
    border: `2px dashed ${dragActive ? 'var(--color-primary)' : 'var(--color-border)'}`,
    borderRadius: 'var(--radius-lg)',
    padding: '3rem 2rem',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 150ms',
    background: dragActive ? 'rgba(59,130,246,0.04)' : 'transparent',
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Upload</h1>
        <p className="page-subtitle">Upload files to AWS S3</p>
      </div>

      <div className="card">
        <div
          style={dropZoneStyle}
          onDragEnter={onDrag}
          onDragOver={onDrag}
          onDragLeave={onDrag}
          onDrop={onDrop}
          onClick={() => document.getElementById('file-input').click()}
        >
          <input
            id="file-input"
            type="file"
            style={{ display: 'none' }}
            onChange={(e) => setFile(e.target.files[0])}
          />
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem', opacity: 0.3 }}>↑</div>
          <p style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
            {file ? file.name : 'Drop a file here or click to browse'}
          </p>
          <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
            {file ? `${formatSize(file.size)} — ${file.type || 'unknown type'}` : 'Maximum 50 MB'}
          </p>
        </div>

        {file && (
          <div className="actions actions-right" style={{ marginTop: '1rem' }}>
            <button className="btn btn-outline" onClick={() => setFile(null)} disabled={uploading}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={handleUpload} disabled={uploading}>
              {uploading && <span className="spinner" />}
              Upload
            </button>
          </div>
        )}

        {lastResponse && (
          <div style={{ marginTop: '1.25rem' }}>
            <label className="label">Upload Result</label>
            <pre className="json">{JSON.stringify(lastResponse, null, 2)}</pre>
          </div>
        )}
      </div>
    </>
  );
}
